<script>
/**
 * @typedef {Object} Interval
 * @property {number} id
 * @property {number} competition_project_partner_id
 * @property {string} key
 * @property {string} name
 * @property {string} description
 * @property {number|null} value
 * @property {string|null} submitted_at
 *
 * @typedef {Object} Forecast
 * @property {Object.<string, Interval[]>} initials
 * @property {Object.<string, Object.<number, Interval[]>>} revisions
 * @property {Object.<string, Interval[]>} actuals
 * @property {Object} current
 */

/**
 * @const {Intl.NumberFormatOptions} numberFormatOptions
 */
const numberFormatOptions = {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
}

export default {
  name: "ForecastMatrix",
  props: {
    /** @type (import('vue').PropType<Boolean>) */
    embedded: {
      type: Boolean,
      default: false
    },
    /** @type {import('vue').PropType<String>} */
    projectId: {
      type: String,
      required: true
    },
    /** @type {import('vue').PropType<Number>} */
    projectPartnerId: {
      type: Number,
      required: true
    },
    /** @type {import('vue').PropType<Forecast>} */
    forecast: {
      type: Object,
      required: true
    },
    /** @type {import('vue').PropType<Boolean>} */
    canEdit: {
      type: Boolean,
      default: false
    },

    /** @type {import('vue').PropType<Boolean>} */
    showHeader: {
      type: Boolean,
      default: true
    },

    /** @type {import('vue').PropType<Number>} */
    interventionRate: {
      type: [Number, null],
      default: 100
    },

    /** @type {import('vue').PropType<Number|null>} */
    forVersion: {
      type: [Number, null],
      required: false,
      default: null
    }
  },
  data() {
    return {
      saving: [],
      currentValues: {},
      errors: {},
    };
  },
  computed: {
    /**
     * @return object
     */
    errorList() {
      const errors = {...this.errors};
      delete errors.message;
      return errors;
    },
    /**
     * @return boolean
     */
    hasErrors() {
      return Object.keys(this.errors).length > 0;
    },

    /*
     * @return boolean
     */
    isRestricted() {
      return false
    },

    /*
     * @return boolean
     */
    editable() {
      return (
        this.canEdit &&
        (!this.isRestricted ||
          this.restrictToVersions.includes(this.currentRevision))
      );
    },

    /*
     * @return boolean
     */
    processing() {
      return this.saving?.length > 0;
    },

    /*
     * @return boolean
     */
    hasSubmittedForecast() {
      return this.forecast.initials_completed;
    },

    /*
     * @return boolean
     */
    hasForecast() {
      return this.intervals?.length > 0;
    },

    /*
     * @return object
     */
    initials() {
      return this.forecast.initials || {};
    },

    /*
     * @return {Interval|object}
     */
    revisions() {
      return this.forecast.revisions || {};
    },

    /*
     * @return {Interval|object}
     */
    actuals() {
      return this.forecast.actuals || {};
    },

    /*
     * @return string[]
     */
    intervals() {
      return Object.keys(this.initials);
    },

    /*
     * @return {string, Interval[]}
     */
    current() {
      return this.forecast.current || {};
    },

    /*
     * @return number|null
     */
    currentRevision() {
      if (this.forVersion !== null) {
        return this.forVersion;
      }
      return this.current.revision || null;
    },

    /*
     * @return string|null
     */
    currentMonth() {
      return this.current.month || null;
    },

    /*
     * @return {key: string, name: string, description: string}[]
     */
    categories() {
      if (!this.hasForecast) {
        return [];
      }

      return this.initials[this.intervals[0]].map(interval => ({
        key: interval.key,
        name: interval.name,
        description: interval.description
      }));
    },

    /*
     * @return boolean
     */
    allValuesProvided() {
      const values = Object.values(this.currentValues)
        .flat()
        .map(interval => interval.value);
      return values.filter(v => v == null || v == undefined)?.length == 0;
    },

  },
  created() {
    if (!this.hasSubmittedForecast || !this.currentRevision) {
      this.setCurrentValues(this.initials);
      return;
    }
    this.setCurrentValues({
      ...this.actuals,
      ...this.revisions[this.currentRevision]
    });
  },
  methods: {
    /**
     * @param number forecast_id
     * @param string forecast_key
     * @return null|string
     */
    getError(forecast_id, forecast_key) {
      return this.errors[forecast_id + '_' + forecast_key]?.[0] || null;
    },

    /**
     * @param string error_id
     * @return string
     */
    getErrorLabel(error_id) {
      const [forecast_id, forecast_key] = error_id.split('_');
      if (!forecast_id && !forecast_key) return 'Unknown field';
      if(!forecast_key) return forecast_id;

      const interval = Object.values(this.currentValues)
        .flat()
        .find(interval => interval.id == forecast_id);

      let [year, month] = interval.start_date.split("-");
      return interval ? `(${month}/${year.slice(2,4)}) ${interval.name}` : 'Unknown field';
    },

    /**
     * @param object data
     * @return void
     */
    setCurrentValues(data) {
      this.$set(this, "currentValues", data);
    },

    /**
     * @param string key
     * @return number
     */
    currentIntervalIndex(key) {
      if (!this.hasSubmittedForecast) return -1;
      return this.intervals.findIndex(interval => interval === key);
    },

    /**
     * @param string key
     * @return Forecast
     */
    getCurrentIntervalForKey(key) {
      return this.currentValues[key]?.[0]?.type || "initial";
    },

    /**
     * @param interval
     * @param key
     * @return object|null
     */
    getCurrentValueForKey(interval, key) {
      return interval.find(i => i.key == key);
    },

    /**
     * @param string intervalKey
     * @return string|number
     */
    totalForecastForInterval(intervalKey, withInterventionRate = false) {
      let values = Object.values(
        this.currentValues[intervalKey] || {}
      ).reduce((total, interval) => {
        return total + (interval.value || 0);
      }, 0);

      values = withInterventionRate ? values * (this.interventionRate / 100) : values;

      return values
        ? values.toLocaleString("en-GB", numberFormatOptions)
        : "--";
    },

    /*
     * @param number intervalID
     * @return boolean
     */
    isSaving(intervalID) {
      return this.saving.includes(intervalID);
    },

    /**
     * @param string categoryKey
     * @return string|number
     */
    totalForecastForCategory(categoryKey, withInterventionRate = false) {
      let values = Object.values(this.currentValues)
        .flat()
        .filter(interval => interval.key === categoryKey)
        .reduce((total, interval) => {
          return total + (interval.value || 0);
        }, 0);

      values = withInterventionRate ? values * (this.interventionRate / 100) : values;

      return values ? values.toLocaleString('en-GB', numberFormatOptions) : "--";
    },

    /*
     * @param boolean withInterventionRate
     * @return string|number
     */
    total(withInterventionRate = false) {
      let values = Object.values(this.currentValues)
        .flat()
        .reduce((total, interval) => {
          return total + (interval.value || 0);
        }, 0);

      values = withInterventionRate ? values * (this.interventionRate / 100) : values;

      return values
        ? values.toLocaleString("en-GB", numberFormatOptions)
        : "--";
    },

    /**
     * @param string month
     * @param object interval
     * @return void
     */
    updateOverheadIntervalForMonth(month, interval) {
      if (interval.key !== "labour") return;
      this.currentValues[month].find(i => i.key === "overheads").value =
        interval.value * .2;

      this.updateInterval({
        key: month,
        interval: this.getCurrentValueForKey(this.currentValues[month], "overheads")
      });
    },

    updateInterval({ key, interval }) {
      if (this.isSaving(interval.id) || !this.editable) return;

      const currentValue = this.getCurrentValueForKey(
        this.currentValues[key],
        interval.key
      );
      if (interval.value === currentValue) return;

      // manually update the overheads value for labour intervals
      this.updateOverheadIntervalForMonth(key, interval);

      // Do API Patch call to update the interval
      this.saving.push(interval.id);

      // Persist the change to the backend
      axios.patch( route("projects.forecast.update", { project: this.projectId }), interval)
        .then((res) => {
          // clear errors
          this.$set(this, "errors", {});
          // Update the current value with any changes made by the backend (e.g. rounding)
          Object.assign(currentValue, res.data);
        })
        .catch((err) => {
          // Revert the value back to the original value on error
          this.$set(this, "errors", err.response?.data?.errors || { general: ["An error occurred while saving the forecast. Please try again."] });
          this.$set(this.errors, 'message', err.response?.data?.message || "An error occurred while saving the forecast. Please try again.");
        })
        .finally(() => {
          this.saving = this.saving.filter(id => id !== interval.id);
        });
    },

    /**
     * @return void
     */
    submitForecast() {
      if (this.processing || !this.allValuesProvided || !this.editable) return;
      this.saving = Object.values(this.currentValues)
        .flat()
        .map(interval => interval.id);

      // If the forecast has already been submitted, we want to submit a new revision, otherwise we want to submit the initial forecast
      const version = this.hasSubmittedForecast ? this.currentRevision || 0 : 0;
      axios.put(
        route("projects.forecast.store", { project: this.projectId }),
        {
          project_partner_id: this.projectPartnerId,
          version,
        }
      )
      .then((res) => {
        // clear errors
        this.$set(this, "errors", {});

        // extract the actuals and revisions from the response and update the current values to reflect any changes made by the backend (e.g. setting submitted_at)
        const { actuals, revisions } = res.data;
        this.setCurrentValues({
          ...actuals,
          ...revisions[this.currentRevision]
        });
        this.$emit('submitted', res.data);
      })
      .catch((err) => {
        this.$set(this, "errors", err.response?.data?.errors || { general: ["An error occurred while submitting the forecast. Please try again."] });
        this.$set(this.errors, 'message', err.response?.data?.message || "An error occurred while submitting the forecast. Please try again.");
      })
      .finally(() => {
        this.saving = [];
      });
    }
  }
};
</script>

<template>
  <div
    v-if="hasForecast"
    id="financial-forecast"
    :class="embedded ? '' : 'container overflow-x-scroll lg:overflow-x-visible my-12 relative'"
  >
    <h3
      v-if="showHeader && !embedded"
      class="mb-6"
    >
      Financial Matrix
    </h3>
    <div v-if="hasErrors" class="mb-4 p-4 bg-negative-100 text-negative-800 rounded text-xs">
      <b>{{ errors.message }}</b>
      <dl
        style="display: grid; grid-template-columns: max-content 1fr; column-gap: 1rem;"
      >
        <template
          v-for="(error, field) in errorList"
        >
          <dt
            :key="field"
            class="font-bold"
          >
            {{ getErrorLabel(field) }}:
          </dt>
          <dd :key="field + '_desc'">
            {{ error[0] }}
          </dd>
        </template>
      </dl>
    </div>
    <div class="w-full min-w-xl lg:min-w-0 pr-6 lg:pr-0">
      <div class="flex items-baseline">
        <div class="w-32 flex-no-shrink py-2 px-4 whitespace-no-wrap">
          <h2
            v-if="!showHeader || embedded"
            class="text-base whitespace-no-wrap"
          >
            Financial Forecast
          </h2>
        </div>
        <div class="w-full flex justify-end">
          <div
            v-for="(interval, index) in intervals"
            :key="interval"
            :class="[
              'text-center text-sm',
              {
                'text-grey-600': currentIntervalIndex(currentMonth) != index,
                'text-primary-600': currentIntervalIndex(currentMonth) == index
              }
            ]"
            :style="{
              width: '6.25rem'
            }"
          >
            <span
              class="block font-bold"
            >
              {{ interval }}
            </span>
            <span class="block text-xs capitalize">
              {{ getCurrentIntervalForKey(interval) }}
            </span>
          </div>
          <div
            :class="['text-center text-sm font-bold']"
            :style="{
              width: '6.25rem'
            }"
          >
            <span
              class="inline-block"
            >
              Total
            </span>
            <span class="block text-xs capitalize">
              Category
            </span>
          </div>
          <div
            :class="['text-center text-sm font-bold']"
            :style="{
              width: '6.25rem'
            }"
          >
            <span
              class="inline-block"
            >
              Grant Total
            </span>
            <span class="block text-xs capitalize">
              ({{ interventionRate }}%)
            </span>
          </div>
        </div>
      </div>
    </div>

    <div class="card is-borderless mt-2 min-w-xl lg:min-w-0 mr-6 lg:mr-0">
      <div
        v-for="{ key, name, description } in categories"
        :key="key"
        class="flex justify-between border-b border-grey-300"
      >
        <div
          class="w-32 flex-no-shrink py-2 px-4 whitespace-no-wrap self-center"
        >
          {{ name }}
          <span
            v-if="description"
            class="ml-1 w-4 h-4 align-top inline-block"
            style="cursor: help;"
            :title="description"
          >
            <icon
              icon="information"
              class="block icons-primary"
            />
            <span class="sr-only">Information</span>
          </span>
        </div>
        <div
          v-if="Object.keys(currentValues).length"
          class="w-full flex justify-end"
        >
          <forecast-matrix-cell
            v-for="(interval, month) in currentValues"
            :key="getCurrentValueForKey(interval, key).id"
            :interval="getCurrentValueForKey(interval, key)"
            :is-current="currentMonth === month"
            :can-edit="key == 'overheads' ? false : editable"
            :current-revision="currentRevision"
            :current-month="month"
            :saving="isSaving(getCurrentValueForKey(interval, key))"
            :error="getError(getCurrentValueForKey(interval, key).id, key)"
            @update="updateInterval"
          />

          <!-- Totals for category -->
          <div
            :class="[
              'flex text-sm font-bold items-center justify-between py-2 border-l-4 border-grey-800 bg-grey-900',
              {
                'text-white': true
                // 'bg-neutral-100': isEditable && !hasValue,
              }
            ]"
            :style="{
              width: '6.25rem'
            }"
          >
            <span class="px-2">£</span>
            <span class="pr-2"> {{ totalForecastForCategory(key) }} </span>
          </div>

          <!-- Grant total for category -->
          <div
            :class="[
              'flex text-sm font-bold items-center justify-between py-2 border-l-4 border-grey-800 bg-grey-900',
              {
                'text-white': true
                // 'bg-neutral-100': isEditable && !hasValue,
              }
            ]"
            :style="{
              width: '6.25rem'
            }"
          >
            <span class="px-2">£</span>
            <span class="pr-2"> {{ totalForecastForCategory(key, true) }} </span>
          </div>
        </div>
      </div>

      <!-- Totals for interval Row -->
      <div class="flex justify-between bg-grey-900 border-b-2 border-grey-800">
        <div
          class="w-32 flex-no-shrink py-2 px-4 whitespace-no-wrap font-bold text-white"
        >
          Total
        </div>
        <div class="w-full flex justify-end">
          <div
            v-for="interval in intervals"
            :key="interval"
            :class="[
              'flex text-sm font-bold text-white items-center justify-between p-2 border-l-4 border-grey-800'
            ]"
            :style="{
              width: '6.25rem'
            }"
          >
            <span>£</span>
            <span>{{ totalForecastForInterval(interval) }}</span>
          </div>

          <!-- Total for all intervals row -->
          <div
            :class="[
              'flex text-sm font-bold text-white items-center justify-between p-2 border-l-4 border-grey-800'
            ]"
            :style="{
              width: '6.25rem'
            }"
          >
            <span>£</span>
            <span>{{ total() }}</span>
          </div>

          <!-- Grant Total for Row -->
          <div
            :class="[
              'flex text-sm font-bold text-white items-center justify-between p-2 border-l-4 border-grey-800'
            ]"
            :style="{
              width: '6.25rem'
            }"
          >
            <span>£</span>
            <span>{{ total(true) }}</span>
          </div>
        </div>
      </div>

      <!-- Grant totals for interval row -->
      <div :class="['flex bg-grey-900', embedded ? '' : 'rounded-b']">
        <div
          class="w-32 flex-no-shrink py-2 px-4 whitespace-no-wrap font-bold text-white"
        >
          Grant Total
        </div>
        <div class="w-full flex justify-end">
          <div
            v-for="interval in intervals"
            :key="interval"
            :class="[
              'flex text-sm font-bold text-white items-center justify-between p-2 border-l-4 border-grey-800'
            ]"
            :style="{
              width: '6.25rem'
            }"
          >
            <span>£</span>
            <span>{{ totalForecastForInterval(interval, true) }}</span>
          </div>
          <div
            :class="[
              'flex text-sm font-bold text-white items-center justify-between p-2 border-l-4 border-grey-800'
            ]"
            :style="{
              width: '6.25rem'
            }"
          >
            <span>£</span>
            <span>{{ total(true) }}</span>
          </div>

          <div
            :class="[
              'flex text-sm font-bold text-white items-center justify-between p-2 border-l-4 border-grey-800'
            ]"
            :style="{
              width: '6.25rem'
            }"
          >
            <span>£</span>
            <span>{{ total(true) }}</span>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="editable && !hasSubmittedForecast"
      class="mt-4 max-w-xs ml-auto"
    >
      <app-button
        as="button"
        :disabled="!allValuesProvided || processing"
        @click="submitForecast"
      >
        Submit Initial Forecast
      </app-button>
    </div>
  </div>
</template>
